"use client";

import { useEffect } from "react";
import { GlobalOperations } from "@/components/global-operations";
import { SectionLabel } from "@/components/section-label";
import Image from "next/image";
import Footer from "@/components/footer";

export default function ServicesClient() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="flex min-h-screen flex-col bg-background">
      {/* Section 1 – Our Services (Text → Image; desktop: text left, image right) */}
      <section className="py-8 md:py-12">
        <div className="mx-auto max-w-[1200px] px-4 md:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
            {/* Left Text */}
            <div className="lg:w-[50%] flex flex-col justify-center space-y-4 md:space-y-6 text-center lg:text-left">
              <h1 className="text-2xl sm:text-3xl md:text-5xl text-[var(--mmi-heading)]">
                Services
              </h1>

              <div className="flex justify-center lg:justify-start">
                <div className="w-24 md:w-32 h-1 bg-[var(--brand-yellow)]" />
              </div>
              <p className="text-sm sm:text-base md:text-lg text-[var(--mmi-body)] leading-relaxed">
                Manufacturing support covering inventory management, quality
                control, and global operations. Programs executed using
                ERP-based traceability, supplier coordination, and
                inspection-controlled workflows aligned to customer
                specifications.
              </p>
            </div>
            {/* Right Placeholder Card (bigger on mobile) */}
            <div className="w-full lg:w-[35%] mx-auto lg:mx-0">
              <div className="relative w-full aspect-[4/3] rounded-[24px] overflow-hidden shadow-sm">
                <img
                  src="/services/services.jpg"
                  alt="Casting process"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Alternating Capability Sections ===== */}
      <section className="w-full py-8 md:py-14 space-y-12 md:space-y-20">
        <div className="mx-auto w-full max-w-[1200px] px-6 sm:px-8 md:px-10 space-y-12 md:space-y-20">
          {/* ===== Section 1 – Inventory Management Systems ===== */}
          <div className="grid items-center gap-8 md:grid-cols-2 md:gap-12">
            {/* Text */}
            <div className="md:order-2">
              <SectionLabel>Inventory Management Systems</SectionLabel>
              <h2 className="mt-2 text-[18px] md:text-2xl font-semibold">
                Flexible programs. Global integration. Inventory your way.
              </h2>
              <p className="mt-3 md:mt-4 text-[13.5px] md:text-base leading-[1.7] text-(--mmi-body)">
                MMI supports customer-defined inventory and replenishment
                models, including Kanban, Blanket Orders, JIT, Hold-for-Release,
                and Consignment. Operations are aligned to existing customer
                planning and ERP processes to manage raw materials,
                subassemblies, and finished goods. ISO 9001:2015–certified
                facilities in the US, China, and India provide controlled
                storage, customer-specific packaging and labeling, and scheduled
                dispatch based on release signals. Scalable warehousing capacity
                and program-level configuration are applied to match demand
                variability, lead-time requirements, and delivery windows.
              </p>
            </div>

            {/* Image */}
            <div className="md:order-1">
              <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl ring-1 ring-inset ring-(--mmi-img-ring)">
                <Image
                  src="/services/services-inventory.jpg"
                  alt="Inventory Management Systems"
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  priority
                />
              </div>
            </div>
          </div>

          {/* ===== Section 2 – Process Feasibility & Execution ===== */}
          <div className="grid items-center gap-8 md:grid-cols-2 md:gap-12">
            {/* Text */}
            <div className="md:order-1">
              <SectionLabel>
                Engineering & Manufacturing Management
              </SectionLabel>
              <h2 className="mt-2 text-[18px] md:text-2xl font-semibold">
                Process Feasibility & Execution
              </h2>
              <p className="mt-3 md:mt-4 text-[13.5px] md:text-base leading-[1.7] text-(--mmi-body)">
                Engineering and manufacturing support spans assembly and
                fabrication, stamping, CNC machining, casting, molding, forging,
                tube bending, and finishing. Capabilities include
                design-for-manufacture review, process feasibility assessment,
                material and tolerance evaluation, tooling coordination, and
                prototype or pilot build support, all executed in alignment with
                customer drawings and revision controls.
              </p>
            </div>

            {/* Image */}
            <div className="md:order-2">
              <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl ring-1 ring-inset ring-(--mmi-img-ring)">
                <Image
                  src="/services/process.jpg"
                  alt="Process Feasibility & Execution"
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              </div>
            </div>
          </div>

          {/* ===== Section 3 – Logistics & Delivery ===== */}
          <div className="grid items-center gap-8 md:grid-cols-2 md:gap-12">
            {/* Text */}
            <div className="md:order-2">
              <SectionLabel>Distribution Control</SectionLabel>
              <h2 className="mt-2 text-[18px] md:text-2xl font-semibold">
                Logistics & Delivery Coordination
              </h2>
              <p className="mt-3 md:mt-4 text-[13.5px] md:text-base leading-[1.7] text-(--mmi-body)">
                Logistics and delivery coordination are managed for domestic and
                international programs across the manufacturing and supplier
                network. Services cover packaging and labeling to customer
                standards, shipment planning, documentation, and delivery to
                customer, third-party, or line-side locations, executed in line
                with purchase orders, release schedules, and defined delivery
                windows.
              </p>
            </div>

            {/* Image */}
            <div className="md:order-1">
              <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl ring-1 ring-inset ring-(--mmi-img-ring)">
                <Image
                  src="/services/logistic.jpeg"
                  alt="logistic"
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  priority
                />
              </div>
            </div>
          </div>

          {/* ===== Section 4 – Global Operations ===== */}
          <div className="grid items-center gap-8 md:grid-cols-2 md:gap-12">
            {/* Text */}
            <div className="md:order-1">
              <SectionLabel>Global Operations</SectionLabel>
              <h2 className="mt-2 text-[18px] md:text-2xl font-semibold">
                Global presence. Unified execution.
              </h2>
              <p className="mt-3 md:mt-4 text-[13.5px] md:text-base leading-[1.7] text-(--mmi-body)">
                MMI operates a fully integrated production network across the
                US, India, China, Vietnam and South Korea, utilizing each
                region’s specialized capabilities. Final Assembly, Quality
                Validation, and Program Integration are centralized in India,
                ensuring consistency, precision, and efficiency across every
                project.
                <br />
                With a proven Global Infrastructure, MMI delivers seamless
                execution and dependable delivery at scale.
              </p>
            </div>

            {/* Image */}
            <div className="md:order-2">
              <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl ring-1 ring-inset ring-(--mmi-img-ring)">
                <Image
                  src="/services/GLOBAL.png"
                  alt="Process Feasibility & Execution"
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Operations */}
      {/* <GlobalOperations /> */}
      <Footer />
    </main>
  );
}
